import React, { useContext, useMemo } from "react";
import { Alert, Table, Tabs } from "antd";
import ReactResizeDetector from "react-resize-detector";
import { makeTableSorterFn, numberWithCommas } from "../../utils";
import AlignRightText from "../map/widgets/AlignRightText";
import SpectrumContext from "./SpectrumContext";
import ExportExcelButton from "../../utils/export";
import TabTitles from "./TabTitles";
import styles from "./ChartContainer.module.scss";

export default function ChartContainer() {
  const {
    activePageTab,
    setActivePageTab,
    containerRef,
    setChartWidth,
    statsByAlmBusNmAndDistrictLv1,
    activeDistrictLv1CodeList,
    bandwidthKHzDict,
    sidoList,
  } = useContext(SpectrumContext);

  const tableDataSource = useMemo(() => {
    return Object.entries(statsByAlmBusNmAndDistrictLv1)
      .map(([alm_bus_nm, statsByDistrictLv1], index) => {
        // alm_bus_nm별로 district_lv1에서는 모두 같은 값을 가집니다.
        const bandwidthHzByDistrictLv1 = Object.keys(statsByDistrictLv1).reduce(
          (acc, districtLv1Code) => {
            acc[districtLv1Code] =
              typeof bandwidthKHzDict[alm_bus_nm] === "string" &&
              bandwidthKHzDict[alm_bus_nm].replace("-", "").length > 0
                ? Number(
                    (
                      Number(bandwidthKHzDict[alm_bus_nm].replace(",", "")) /
                      1000
                    ).toFixed(2),
                  )
                : 0.0;
            return acc;
          },
          {},
        );

        const densityByDistrictLv1 = Object.keys(statsByDistrictLv1).reduce(
          (acc, districtLv1Code) => {
            const size = statsByDistrictLv1[districtLv1Code];
            let bandwidth = bandwidthHzByDistrictLv1[districtLv1Code];
            let density = 0.0;
            if (size && bandwidth) {
              bandwidth = parseFloat(bandwidth.toString().replace(",", ""));
              if (bandwidth) {
                density =
                  bandwidth > 0 ? Number((size / bandwidth).toFixed(2)) : 0.0;
              }
            }
            acc[districtLv1Code] = Number((density / 1000).toFixed(2));
            return acc;
          },
          {},
        );

        return [
          {
            key: index,
            usage: alm_bus_nm,
            rowType: "국수",
            ...statsByDistrictLv1,
          },
          {
            key: `${index}-2`,
            usage: alm_bus_nm,
            rowType: "대역",
            ...bandwidthHzByDistrictLv1,
          },
          {
            key: `${index}-3`,
            usage: alm_bus_nm,
            rowType: "밀도",
            ...densityByDistrictLv1,
          },
        ];
      })
      .flat();
  }, [statsByAlmBusNmAndDistrictLv1, bandwidthKHzDict]);

  const tableColumns = useMemo(() => {
    return [
      {
        title: "용도",
        dataIndex: "usage",
        key: "용도",
        width: 300,
        fixed: "left",
          style:{backgroundColor: "black"},
        sorter: makeTableSorterFn("usage"),
        render: (usage, row, index) => {
          const obj = { children: usage, props: {} };
          if (index % 3 === 0) {
            obj.props.rowSpan = 3;
          } else {
            obj.props.rowSpan = 0;
          }
          return obj;
        },
        className: "bgColor"
      },
      {
        title: "항목",
        dataIndex: "rowType",
        key: "항목",
        width: 80,
        fixed: "left",
        className: "bgColor2"
      },
      {
        title: "소계",
        dataIndex: "total",
        key: "소계",
        width: 100,
        fixed: "left",
        render: text => AlignRightText({ text: numberWithCommas(text) }),
      },
      ...sidoList
        .filter(({ value }) => activeDistrictLv1CodeList.includes(value))
        .map(({ value, label }) => ({
          title: label,
          dataIndex: value,
          key: label,
          width: 130,
          render: (text, record) => {
            const textValue =
              typeof text === "number" && record.rowType !== "국수"
                ? text.toFixed(2)
                : text.toFixed(0);
            return AlignRightText({ text: numberWithCommas(textValue) });
          },
        })),
    ];
  }, [sidoList, activeDistrictLv1CodeList]);

  return (
    <div>
      <div
      // hidden={Object.keys(statsByAlmBusNmAndDistrictLv1).length === 0}
      >
        <TabTitles
          onChange={activeKey => setActivePageTab(activeKey)}
          style={{ marginBottom: "1em" }}
        />

        <Tabs
          activeKey={activePageTab + ""}
          type={"card"}
          tabBarStyle={{ display: "none" }}
        >
          <Tabs.TabPane tab={"그래프 보기"} key={"0"}>
            <div ref={containerRef}>
              <ReactResizeDetector handleWidth onResize={setChartWidth} />
            </div>
            <Alert
              message={"네비게이션 바"}
              description={
                "위 네비게이션 바의 크기를 조절하거나 위치를 옮겨, 조회할 주파수 범위를 변경하실 수 있습니다."
              }
              type={"info"}
              showIcon={true}
            />
          </Tabs.TabPane>
          <Tabs.TabPane tab={"테이블 보기"} key={"1"}>
            <div className={styles.exportWrapper}>
              <ExportExcelButton
                type="primary"
                filename={"주파수 이용현황"}
                data={tableDataSource}
                columns={tableColumns}
              >
                엑셀 다운로드
              </ExportExcelButton>
            </div>
            <Table
              scroll={{ x: 300 + 70 + activeDistrictLv1CodeList.length * 120, y: 700 }}
              pagination={false}
              className={"minWidthUnset"}
              columns={tableColumns}
              dataSource={tableDataSource}
            ></Table>
          </Tabs.TabPane>
        </Tabs>
      </div>
    </div>
  );
}
